import type { SupplierOperation } from '@tandemone/types'

import { Badge } from '@/components/ui/badge'
import { SUPPLIER_OPERATION_OPTIONS } from '@/domains/supplier-onboarding'

import {
  type OperationFamily,
  getOperationFamily,
  OPERATION_FAMILY_LABELS,
  OPERATION_FAMILY_ORDER
} from './operation-family'
import { ProfileEmpty } from './profile-empty'
import { ProfileSection } from './profile-section'

const OPERATION_FAMILY_DOT_CLASSES: Record<OperationFamily, string> = {
  machining: 'bg-indigo-400',
  fabrication: 'bg-sky-400',
  joining: 'bg-rose-400',
  electronics: 'bg-teal-400',
  assembly: 'bg-orange-400',
  materials: 'bg-stone-400'
}

/** legacy flat-operation render for suppliers that predate the capability
 *  taxonomy and have not re-selected yet. grouped by the 6 operation families. */
export function LegacyProfileOperations({
  companyName,
  rows
}: {
  companyName: string
  rows: SupplierOperation[]
}) {
  const filtered = rows.filter(row => row.operation !== 'none_of_the_above')
  const byFamily = new Map<OperationFamily, SupplierOperation[]>()
  for (const row of filtered) {
    const { family } = getOperationFamily(row.operation)
    const list = byFamily.get(family) ?? []
    list.push(row)
    byFamily.set(family, list)
  }
  return (
    <ProfileSection title={`${companyName} Operations & Capabilities`}>
      {filtered.length === 0 ? (
        <ProfileEmpty message='No operations listed.' />
      ) : (
        <div className='flex flex-col gap-5'>
          {OPERATION_FAMILY_ORDER.filter(family => byFamily.has(family)).map(
            family => (
              <div key={family} className='space-y-2'>
                <div className='flex items-center gap-2'>
                  <span
                    className={`inline-block size-2 rounded-full ${OPERATION_FAMILY_DOT_CLASSES[family]}`}
                  />
                  <h3 className='text-xs uppercase tracking-wide text-muted-foreground'>
                    {OPERATION_FAMILY_LABELS[family]}
                  </h3>
                </div>
                <div className='flex flex-col divide-y divide-border'>
                  {byFamily
                    .get(family)!
                    .slice()
                    .sort(
                      (left, right) =>
                        right.yearsInBusiness - left.yearsInBusiness
                    )
                    .map(row => {
                      const option = SUPPLIER_OPERATION_OPTIONS[row.operation]
                      return (
                        <div
                          key={row.id}
                          className='flex flex-col gap-1.5 py-1.5 sm:grid sm:grid-cols-[1fr_auto_auto] sm:items-center sm:gap-0'
                        >
                          <div className='flex items-center justify-between gap-2 sm:contents'>
                            <span className='text-sm text-foreground sm:order-1'>
                              {option.label}
                            </span>
                            <span className='shrink-0 text-xs tabular-nums text-ink-soft sm:order-3 sm:pl-4 sm:text-right'>
                              {row.yearsInBusiness} yrs
                            </span>
                          </div>
                          <span className='flex flex-wrap items-center gap-2 sm:order-2 sm:justify-end sm:pl-3'>
                            <Badge
                              variant='outline'
                              className={`w-28 justify-center ${
                                row.inHouse
                                  ? 'border-emerald-300 text-emerald-800'
                                  : 'border-border text-muted-foreground/40'
                              }`}
                            >
                              in-house
                            </Badge>
                            <Badge
                              variant='outline'
                              className={`w-28 justify-center ${
                                row.subContracted
                                  ? 'border-amber-300 text-amber-800'
                                  : 'border-border text-muted-foreground/40'
                              }`}
                            >
                              subcontracted
                            </Badge>
                          </span>
                        </div>
                      )
                    })}
                </div>
              </div>
            )
          )}
        </div>
      )}
    </ProfileSection>
  )
}
